/**
 * Board-related constants shared between client, server, and database packages.
 */

import { GQL } from '@aline/graphql';
import { generateKeyBetween, generateNKeysBetween } from 'jittered-fractional-indexing';

export type ColumnCategoryType = GQL.ColumnCategory;

/**
 * Category display order for sorting columns on a board.
 * Lower index = appears earlier in the board.
 */
export const COLUMN_CATEGORY_ORDER: ColumnCategoryType[] = [
  GQL.ColumnCategory.Triage,
  GQL.ColumnCategory.NotStarted,
  GQL.ColumnCategory.Started,
  GQL.ColumnCategory.Done,
  GQL.ColumnCategory.Paused,
  GQL.ColumnCategory.Cancelled,
];

/**
 * Category precedence map for efficient sorting.
 * Lower number = higher priority (appears earlier).
 */
export const COLUMN_CATEGORY_PRECEDENCE: Record<ColumnCategoryType, number> = {
  [GQL.ColumnCategory.Triage]: 0,
  [GQL.ColumnCategory.NotStarted]: 1,
  [GQL.ColumnCategory.Started]: 2,
  [GQL.ColumnCategory.Done]: 3,
  [GQL.ColumnCategory.Paused]: 4,
  [GQL.ColumnCategory.Cancelled]: 5,
};

/**
 * Default column colors by category, matching the category icon fill colors.
 */
export const COLUMN_CATEGORY_COLORS: Record<ColumnCategoryType, string> = {
  [GQL.ColumnCategory.Triage]: '#7B61FF', // Purple
  [GQL.ColumnCategory.NotStarted]: '#146EE8', // Blue
  [GQL.ColumnCategory.Started]: '#E87A14', // Orange
  [GQL.ColumnCategory.Done]: '#12B066', // Green
  [GQL.ColumnCategory.Paused]: '#F4BB2B', // Yellow
  [GQL.ColumnCategory.Cancelled]: '#FF4059', // Red
};

/**
 * Default column color used when category is unknown or as a fallback.
 */
export const DEFAULT_COLUMN_COLOR = COLUMN_CATEGORY_COLORS[GQL.ColumnCategory.NotStarted];

/**
 * Default board color used when creating new boards.
 */
export const DEFAULT_BOARD_COLOR = DEFAULT_COLUMN_COLOR;

/**
 * Colors offered for boards and columns. Category and board defaults are referenced directly so
 * every default always has a corresponding selected state in the picker. Deduped because the
 * board default is itself a category color: a repeated swatch reads as two options that mean the
 * same thing, and both light up as selected when either is picked.
 */
export const BOARD_COLOR_PALETTE = [
  ...new Set([
    DEFAULT_BOARD_COLOR,
    '#0747A6', // Dark Blue
    '#79F2C0', // Mint
    '#57D9A3', // Green
    '#6554C0', // Purple
    '#B3BAC5', // Gray
    '#FFAB00', // Yellow
    '#FF8B00', // Orange
    '#FF5630', // Red Orange
    '#FFBDAD', // Light Orange
    '#E53935', // Red
    '#FFC0CB', // Pink
    '#DE350B', // Dark Red
    '#FFEBE6', // Light Pink
    ...Object.values(COLUMN_CATEGORY_COLORS),
  ]),
];

/**
 * Categories that can absorb a deleted column's workflows and phase homes when the deleted
 * column's own category is left with nothing. Triage is a singleton and the last Not Started
 * column has no sibling, so both hand off to the neighbouring unstarted and started categories;
 * every board keeps at least one non-signing Started column, so a target always exists. The
 * terminal categories keep their work in place: a board cannot lose its last Done, Paused or
 * Cancelled column, so their own category is always available.
 */
export const COLUMN_DELETE_FALLBACK_CATEGORIES: Record<ColumnCategoryType, ColumnCategoryType[]> = {
  [GQL.ColumnCategory.Triage]: [GQL.ColumnCategory.NotStarted, GQL.ColumnCategory.Started],
  [GQL.ColumnCategory.NotStarted]: [GQL.ColumnCategory.Triage, GQL.ColumnCategory.Started],
  [GQL.ColumnCategory.Started]: [GQL.ColumnCategory.Started],
  [GQL.ColumnCategory.Done]: [GQL.ColumnCategory.Done],
  [GQL.ColumnCategory.Paused]: [GQL.ColumnCategory.Paused],
  [GQL.ColumnCategory.Cancelled]: [GQL.ColumnCategory.Cancelled],
};

interface ColumnDeleteTarget {
  category: ColumnCategoryType;
  id: string;
}

/**
 * The columns a delete may move workflows and phase homes to, in board order. Work stays within
 * its own category whenever the category has another column; only a category the delete would
 * empty falls back to {@link COLUMN_DELETE_FALLBACK_CATEGORIES}. Signing is never offered: it
 * only ever holds signing work.
 *
 * Both the picker and the server's validation read from here, so a column the user can choose is
 * a column the delete will accept.
 */
export function getColumnDeleteTargetColumns<TColumn extends ColumnDeleteTarget>(args: {
  columns: TColumn[];
  deletedColumn: ColumnDeleteTarget;
  signingColumnId?: string | null;
}): TColumn[] {
  const { columns, deletedColumn, signingColumnId } = args;
  const candidates = columns.filter(
    column => column.id !== deletedColumn.id && column.id !== signingColumnId
  );
  const sameCategory = candidates.filter(column => column.category === deletedColumn.category);
  if (sameCategory.length) return sameCategory;

  const fallbackCategories = COLUMN_DELETE_FALLBACK_CATEGORIES[deletedColumn.category];

  return candidates.filter(column => fallbackCategories.includes(column.category));
}

/**
 * Locked names for system-managed singleton columns (`Board.triageColumnId` /
 * `Board.signingColumnId`). Other seeded names (e.g. "Signed") are editable defaults.
 */
export const SystemColumnNames = {
  Signing: 'Signing',
  Triage: 'Triage',
} as const;

/**
 * Get the default color for a column category.
 */
export function getDefaultColumnColor(category: ColumnCategoryType): string {
  return COLUMN_CATEGORY_COLORS[category] ?? DEFAULT_COLUMN_COLOR;
}

/**
 * Lexicographic compare for fractional index keys. Do not use localeCompare —
 * keys are case-sensitive base-62 strings that must sort by code-unit order.
 */
export function compareSortKeys(a: string, b: string): number {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

/**
 * Order columns the way a board displays them: category precedence first, then
 * `sortKey` within the category.
 */
export function compareBoardColumns(a: BoardColumnOrdering, b: BoardColumnOrdering): number {
  const categoryDiff =
    COLUMN_CATEGORY_PRECEDENCE[a.category] - COLUMN_CATEGORY_PRECEDENCE[b.category];
  if (categoryDiff !== 0) return categoryDiff;
  return compareSortKeys(a.sortKey, b.sortKey);
}

interface BoardColumnOrdering {
  category: ColumnCategoryType;
  sortKey: string;
}

/**
 * Generate a jittered fractional key between two neighbors (`null` = open end).
 */
export function generateColumnSortKeyBetween(
  before: string | null | undefined,
  after: string | null | undefined
): string {
  return generateKeyBetween(before, after);
}

/**
 * Generate `n` deterministic fractional keys for seeds / migrations.
 * Uses zero jitter so rewrites are stable across runs.
 */
export function generateColumnSortKeys(n: number): string[] {
  return generateNKeysBetween(null, null, n, { jitterBits: 0 });
}

interface ColumnConfig {
  category: ColumnCategoryType;
  color: string;
  name: string;
}

/**
 * Default columns for signing/contract workflow boards.
 * Signing is seeded here; `Board.signingColumnId` points at the Signing column.
 * Triage is optional at runtime via Add Triage, but included in the default seed.
 * Array order is seed display order — sortKeys are assigned at create time.
 */
export const SIGNING_BOARD_COLUMNS: ColumnConfig[] = [
  {
    category: GQL.ColumnCategory.Triage,
    color: COLUMN_CATEGORY_COLORS[GQL.ColumnCategory.Triage],
    name: SystemColumnNames.Triage,
  },
  {
    category: GQL.ColumnCategory.Started,
    color: COLUMN_CATEGORY_COLORS[GQL.ColumnCategory.Started],
    name: 'Negotiating',
  },
  {
    category: GQL.ColumnCategory.Started,
    color: COLUMN_CATEGORY_COLORS[GQL.ColumnCategory.Started],
    name: SystemColumnNames.Signing,
  },
  {
    category: GQL.ColumnCategory.Started,
    color: COLUMN_CATEGORY_COLORS[GQL.ColumnCategory.Started],
    name: 'Signed',
  },
  {
    category: GQL.ColumnCategory.Done,
    color: COLUMN_CATEGORY_COLORS[GQL.ColumnCategory.Done],
    name: 'Recently Completed',
  },
  {
    category: GQL.ColumnCategory.Paused,
    color: COLUMN_CATEGORY_COLORS[GQL.ColumnCategory.Paused],
    name: 'Paused',
  },
  {
    category: GQL.ColumnCategory.Cancelled,
    color: COLUMN_CATEGORY_COLORS[GQL.ColumnCategory.Cancelled],
    name: 'Cancelled',
  },
];

/**
 * Default columns for regular (non-signing) boards.
 * No signing column until Add signing; Triage included in the default seed.
 * Array order is seed display order — sortKeys are assigned at create time.
 */
export const REGULAR_BOARD_COLUMNS: ColumnConfig[] = [
  {
    category: GQL.ColumnCategory.Triage,
    color: COLUMN_CATEGORY_COLORS[GQL.ColumnCategory.Triage],
    name: SystemColumnNames.Triage,
  },
  {
    category: GQL.ColumnCategory.Started,
    color: COLUMN_CATEGORY_COLORS[GQL.ColumnCategory.Started],
    name: 'Doing',
  },
  {
    category: GQL.ColumnCategory.Done,
    color: COLUMN_CATEGORY_COLORS[GQL.ColumnCategory.Done],
    name: 'Done',
  },
  {
    category: GQL.ColumnCategory.Paused,
    color: COLUMN_CATEGORY_COLORS[GQL.ColumnCategory.Paused],
    name: 'Paused',
  },
  {
    category: GQL.ColumnCategory.Cancelled,
    color: COLUMN_CATEGORY_COLORS[GQL.ColumnCategory.Cancelled],
    name: 'Cancelled',
  },
];
